import { useState, useRef, useEffect } from 'react'
import {
  X, User, Settings, BarChart2, HelpCircle,
  Calendar, Mail, Database, Globe, Zap, Plug,
  ExternalLink, ChevronDown, Cloud, Palette, Monitor,
  AlertCircle, LogOut, Camera
} from 'lucide-react'
import { useApp } from '@/store/AppContext'
import { getTranslations } from '@/i18n'
import { SkillsContent } from './SkillsContent'
import { ConnectorsContent } from './ConnectorsContent'
import { BillingContent as BillingContentExternal } from './BillingContent'
import { ScheduledTasksContent } from './ScheduledTasksContent'
import { DataControlContent } from './DataControlContent'
import { SineMailContent } from './SineMailContent'
import { useAuth } from '@/hooks/useAuth'
import { useCredits } from '@/hooks/useCredits'
import { AgentHistoryPage } from '@/components/agent/AgentHistoryPage'
import { SinePagesContent } from './SinePagesContent'
import { getSupabase } from '@/lib/supabase'

type SettingsTab =
  | 'account' | 'settings' | 'usage' | 'billing'
  | 'scheduled' | 'mail' | 'data' | 'personalization'
  | 'skills' | 'connectors' | 'integrations' | 'agent-history' | 'pages'

export function SettingsModal() {
  const { settingsOpen, setSettingsOpen, settings, updateSettings, settingsInitialTab, setSettingsInitialTab } = useApp()
  const t = getTranslations(settings.language)
  const [activeTab, setActiveTab] = useState<SettingsTab>('settings')
  const { user } = useAuth()
  const { profile } = useCredits(user?.id ?? null)
  const displayName = user?.name || user?.email?.split('@')[0] || 'Bruker'
  const initial = (user?.name || user?.email || 'U')[0].toUpperCase()
  const isPro = profile?.plan === 'pro'

  useEffect(() => {
    if (settingsOpen && settingsInitialTab) {
      setActiveTab((settingsInitialTab === 'billing' ? 'usage' : settingsInitialTab) as SettingsTab)
      setSettingsInitialTab(undefined)
    }
  }, [settingsOpen, settingsInitialTab, setSettingsInitialTab])

  if (!settingsOpen) return null

  const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'account',         label: t.app.account,        icon: <User size={17} /> },
    { id: 'settings',        label: t.app.settings,       icon: <Settings size={17} /> },
    { id: 'usage',           label: t.app.usage,          icon: <BarChart2 size={17} /> },
    { id: 'scheduled',       label: t.settings.scheduledTasks,   icon: <Calendar size={17} /> },
    { id: 'mail',            label: t.settings.emailSine,        icon: <Mail size={17} /> },
    { id: 'data',            label: t.settings.dataControl,      icon: <Database size={17} /> },
    { id: 'personalization', label: t.settings.personalization,  icon: <Palette size={17} /> },
    { id: 'skills',          label: t.settings.skills,           icon: <Zap size={17} /> },
    { id: 'connectors',      label: t.settings.connections,      icon: <Plug size={17} /> },
    { id: 'pages',           label: 'Sine Pages',                icon: <Globe size={17} /> },
    { id: 'integrations',    label: t.settings.integrations,     icon: <ExternalLink size={17} /> },
    { id: 'agent-history',   label: t.settings.agentHistory,     icon: <Calendar size={17} /> },
  ]

  return (
    <div
      className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) setSettingsOpen(false) }}
    >
      <div className="modal-panel animate-fade-in">
        {/* Close button */}
        <button className="modal-close" onClick={() => setSettingsOpen(false)}>
          <X size={15} />
        </button>

        {/* Left sidebar */}
        <div className="modal-sidebar">
          {/* User header */}
          <div className="modal-sidebar-user">
            <div className="settings-user-info">
              <div className="settings-avatar">
                {user?.avatarUrl
                  ? <img src={user.avatarUrl} alt="" className="settings-avatar-img" />
                  : initial
                }
              </div>
              <div className="settings-user-meta">
                <div className="settings-user-name-row">
                  <span className="modal-sidebar-username settings-username-ellipsis">{displayName}</span>
                  {isPro && (
                    <span className="s-badge s-badge-accent settings-pro-badge">PRO</span>
                  )}
                </div>
                <div className="settings-user-email s-text-muted">{user?.email}</div>
              </div>
            </div>
            <ChevronDown size={13} className="s-text-muted settings-chevron" />
          </div>

          {/* Nav items */}
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`modal-sidebar-item${activeTab === tab.id ? ' active' : ''}`}
            >
              <span className={activeTab === tab.id ? 'modal-sidebar-icon-active' : 'modal-sidebar-icon'} style={{ flexShrink: 0 }}>
                {tab.icon}
              </span>
              <span>{tab.label}</span>
            </button>
          ))}

          {/* Get help */}
          <div className="settings-help-row">
            <a href="/hjelp" target="_blank" rel="noopener noreferrer" className="modal-sidebar-item" style={{ textDecoration: 'none' }}>
              <HelpCircle size={17} className="s-text-secondary settings-help-icon" />
              <span>{t.app.getHelp}</span>
              <ExternalLink size={11} className="s-text-muted settings-external-icon" />
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="modal-content">
          {activeTab === 'settings' && (
            <SettingsContent settings={settings} updateSettings={updateSettings} />
          )}
          {activeTab === 'account' && (
            <AccountContent />
          )}
          {activeTab === 'usage' && (
            <BillingContentExternal />
          )}
          {activeTab === 'billing' && (
            <BillingContentExternal />
          )}
          {activeTab === 'personalization' && (
            <PersonalizationContent />
          )}
          {activeTab === 'skills' && (
            <SkillsContent onNavigateToChat={(prompt) => {
              setSettingsOpen(false)
              window.dispatchEvent(new CustomEvent('sine:prefill-chat', { detail: { prompt } }))
            }} />
          )}
          {activeTab === 'connectors' && (
            <ConnectorsContent />
          )}
          {activeTab === 'scheduled' && (
            <ScheduledTasksContent />
          )}
          {activeTab === 'agent-history' && (
            <div className="settings-agent-history-wrapper">
              <AgentHistoryPage />
            </div>
          )}
          {activeTab === 'data' && (
            <DataControlContent />
          )}
          {activeTab === 'mail' && (
            <SineMailContent />
          )}
          {activeTab === 'pages' && (
            <SinePagesContent />
          )}
          {activeTab === 'integrations' && (
            <PlaceholderContent
              title={tabs.find(t => t.id === activeTab)?.label ?? ''}
              description="Denne funksjonen er under utvikling og kommer snart."
            />
          )}
        </div>
      </div>
    </div>
  )
}

function SettingsContent({
  settings,
  updateSettings,
}: {
  settings: { language: 'no' | 'en'; theme: 'dark' | 'light' | 'system' }
  updateSettings: (u: Partial<{ language: 'no' | 'en'; theme: 'dark' | 'light' | 'system' }>) => void
}) {
  const t = getTranslations(settings.language)
  const [emailUpdates, setEmailUpdates] = useState(true)
  const [emailTask, setEmailTask] = useState(true)
  const appearance = settings.theme ?? 'dark'
  const [autoApproveCalendar, setAutoApproveCalendar] = useState(false)
  const [pushNotifications, setPushNotifications] = useState(true)
  const effectiveTheme = settings.theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : settings.theme
  const settingsLogoSrc = effectiveTheme === 'light' ? '/Sine-sort.svg' : '/Sinev6.svg'

  return (
    <div>
      {/* Logo header */}
      <div className="settings-logo-header">
        <img src={settingsLogoSrc} alt="Sine" className="settings-logo-img" />
        <span className="settings-version s-text-muted">versjon 1.0</span>
      </div>

      <h2 className="settings-title">{t.settings.title}</h2>

      {/* General section */}
      <div className="settings-section">
        <p className="settings-section-label">{t.settings.general}</p>

        {/* Language */}
        <div className="settings-row">
          <div>
            <div className="settings-row-label">Språk</div>
          </div>
          <div className="settings-select-wrapper">
            <select
              value={settings.language}
              onChange={e => updateSettings({ language: e.target.value as 'no' | 'en' })}
              className="settings-select"
              style={{ paddingRight: 32, appearance: 'none' as const }}
            >
              <option value="no">Norsk</option>
              <option value="en">English</option>
            </select>
            <ChevronDown size={12} className="settings-select-chevron s-text-secondary" />
          </div>
        </div>

        {/* Appearance */}
        <div className="settings-appearance-section">
          <div className="settings-row-label settings-appearance-label">Utseende</div>
          <div className="settings-theme-picker">
            {[
              { id: 'light' as const,  label: t.settings.light,  icon: <Monitor size={16} /> },
              { id: 'dark' as const,   label: t.settings.dark,   icon: <Monitor size={16} /> },
              { id: 'system' as const, label: 'System', icon: <Cloud size={16} /> },
            ].map(theme => (
              <button
                key={theme.id}
                onClick={() => updateSettings({ theme: theme.id })}
                className="settings-theme-btn"
              >
                <div className={`settings-theme-preview settings-theme-preview--${theme.id}${appearance === theme.id ? ' settings-theme-preview--active' : ''}`}>
                  {theme.id === 'system' ? (
                    <div className="settings-theme-system-split">
                      <div className="settings-theme-system-light" />
                      <div className="settings-theme-system-dark" />
                    </div>
                  ) : (
                    <div className="settings-theme-preview-lines">
                      <div className={`settings-theme-line settings-theme-line--${theme.id} settings-theme-line--w75`} />
                      <div className={`settings-theme-line settings-theme-line--${theme.id} settings-theme-line--w50`} />
                      <div className={`settings-theme-line settings-theme-line--${theme.id} settings-theme-line--w65`} />
                    </div>
                  )}
                </div>
                <span className={`settings-theme-label${appearance === theme.id ? ' settings-theme-label--active' : ''}`}>
                  {theme.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <hr className="s-divider settings-divider" />

      {/* Communication preferences */}
      <div className="settings-section">
        <p className="settings-section-label">{t.settings.communicationPreferences}</p>
        <ToggleSetting
          label={t.settings.receiveUpdates}
          description={t.settings.receiveUpdatesDesc}
          value={emailUpdates}
          onChange={setEmailUpdates}
        />
        <ToggleSetting
          label={t.settings.emailOnTask}
          description={t.settings.emailOnTaskDesc}
          value={emailTask}
          onChange={setEmailTask}
        />
        <ToggleSetting
          label={t.settings.pushNotificationsCalendar}
          description={t.settings.pushNotificationsCalendarDesc}
          value={pushNotifications}
          onChange={setPushNotifications}
        />
      </div>

      <hr className="s-divider settings-divider" />

      {/* Agentic calendar */}
      <div className="settings-section">
        <p className="settings-section-label">{t.settings.language === 'no' ? 'Agentisk kalender' : 'Agentic calendar'}</p>
        <ToggleSetting
          label={t.settings.autoApproveCalendar}
          description={t.settings.autoApproveCalendarDesc}
          value={autoApproveCalendar}
          onChange={setAutoApproveCalendar}
        />
      </div>

      <hr className="s-divider settings-divider" />

      {/* Cookies */}
      <div className="settings-row settings-cookies-row">
        <span className="settings-row-label">Administrer informasjonskapsler</span>
        <button className="settings-ghost-btn">
          Administrer
        </button>
      </div>
    </div>
  )
}

function ToggleSetting({
  label, description, value, onChange,
}: {
  label: string
  description: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="settings-row">
      <div className="settings-toggle-text">
        <div className="settings-row-label">{label}</div>
        <div className="settings-row-desc">{description}</div>
      </div>
      <label className="toggle">
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />
        <span className="toggle-slider" />
      </label>
    </div>
  )
}

function AccountContent() {
  const { settings } = useApp()
  const t = getTranslations(settings.language)
  const { user, signOut, refreshUser } = useAuth()
  const { profile, refreshCredits } = useCredits(user?.id ?? null)
  const displayName = user?.name || user?.email?.split('@')[0] || 'Bruker'
  const initial = (user?.name || user?.email || 'U')[0].toUpperCase()
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url || user?.avatarUrl || null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarMsg, setAvatarMsg] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const API_BASE = import.meta.env.VITE_API_URL ?? ''

  useEffect(() => {
    setAvatarUrl(profile?.avatar_url || user?.avatarUrl || null)
  }, [profile?.avatar_url, user?.avatarUrl])

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingAvatar(true)
    setAvatarMsg('')
    try {
      const formData = new FormData()
      formData.append('file', file)
      const supabaseSession = localStorage.getItem(`sb-${new URL(import.meta.env.VITE_SUPABASE_URL || '').hostname.split('.')[0]}-auth-token`)
      const token = supabaseSession ? JSON.parse(supabaseSession)?.access_token || JSON.parse(supabaseSession)?.currentSession?.access_token : null
      const res = await fetch(`${API_BASE}/api/profile/avatar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      const data = await res.json()
      setAvatarUrl(data.avatar_url)
      setAvatarMsg('Profilbilde oppdatert!')
      refreshCredits()
      refreshUser()
      setTimeout(() => setAvatarMsg(''), 3000)
    } catch (err: unknown) {
      setAvatarMsg('Feil: ' + (err instanceof Error ? err.message : 'Ukjent'))
    }
    setUploadingAvatar(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div>
      <h2 className="settings-title">{t.app.account}</h2>

      {/* Profile card */}
      <div className="settings-profile-card s-bg-panel-2 s-border">
        {/* Clickable avatar with camera overlay */}
        <div
          className="settings-avatar-lg"
          onClick={() => fileInputRef.current?.click()}
          title="Klikk for å endre profilbilde"
        >
          {avatarUrl
            ? <img src={avatarUrl} alt="" className="settings-avatar-img" />
            : <span className="settings-avatar-initial">{initial}</span>
          }
          {/* Camera overlay */}
          <div className={`settings-avatar-overlay${uploadingAvatar ? ' settings-avatar-overlay--visible' : ''}`}>
            {uploadingAvatar
              ? <div className="settings-avatar-spinner" />
              : <Camera size={16} color="white" />
            }
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={handleAvatarUpload}
          />
        </div>
        <div>
          <div className="settings-profile-name s-text-primary">{displayName}</div>
          <div className="settings-profile-email s-text-secondary">{user?.email}</div>
          {avatarMsg && (
            <div className={`settings-avatar-msg ${avatarMsg.startsWith('Feil') ? 's-text-danger' : 's-text-success'}`}>
              {avatarMsg}
            </div>
          )}
        </div>
      </div>

      <div className="settings-section">
        <p className="settings-section-label">{t.settings.profileInfo}</p>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t.settings.emailAddress}</div>
            <div className="settings-row-desc">{user?.email || 'Ikke tilgjengelig'}</div>
          </div>
          <button className="settings-ghost-btn">{t.settings.change}</button>
        </div>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t.settings.password}</div>
            <div className="settings-row-desc">{t.settings.changePasswordDesc}</div>
          </div>
          <button className="settings-ghost-btn">{t.settings.change}</button>
        </div>
      </div>

      <hr className="s-divider settings-divider" />

      <div className="settings-section">
        <p className="settings-section-label">{t.settings.session}</p>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t.settings.logOut}</div>
            <div className="settings-row-desc">{t.settings.logOutDesc}</div>
          </div>
          <button onClick={() => signOut()} className="settings-ghost-btn settings-ghost-btn--icon">
            <LogOut size={13} />
            {t.settings.logOut}
          </button>
        </div>
      </div>

      <hr className="s-divider settings-divider" />

      <div className="settings-section">
        <p className="settings-section-label">{t.settings.dangerZone}</p>
        <div className="settings-row">
          <div>
            <div className="settings-row-label s-text-danger">{t.settings.deleteAccount}</div>
            <div className="settings-row-desc">{t.settings.deleteAccountDesc}</div>
          </div>
          <button className="settings-ghost-btn settings-ghost-btn--danger">{t.settings.delete}</button>
        </div>
      </div>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function UsageContent() {
  const { settings } = useApp()
  const t = getTranslations(settings.language)
  const { user } = useAuth()
  const { profile, transactions } = useCredits(user?.id ?? null)
  const isPro = profile?.plan === 'pro'
  const MONTHLY_LIMIT = isPro ? 500 : 50
  const availableCredits = profile?.credits ?? 0
  const usedCredits = Math.max(0, MONTHLY_LIMIT - availableCredits)
  const usedPct = MONTHLY_LIMIT > 0 ? Math.min(100, (usedCredits / MONTHLY_LIMIT) * 100) : 0

  return (
    <div>
      <h2 className="settings-title">{t.app.usage}</h2>

      {/* Credit balance card */}
      <div className="settings-credit-card s-bg-panel-2 s-border">
        <div className="settings-credit-header">
          <div>
            <div className="settings-credit-label s-text-muted">{t.settings.availableCredits}</div>
            <div className="settings-credit-amount s-text-primary">
              {availableCredits.toLocaleString('no-NO')}
            </div>
          </div>
          <span className={`s-badge ${isPro ? 's-badge-accent' : ''}`}>
            {isPro ? 'Pro' : 'Gratis'}
          </span>
        </div>
        <div className="settings-credit-progress-row">
          <span className="settings-credit-progress-label s-text-secondary">{t.settings.usedThisMonth}</span>
          <span className="settings-credit-progress-count s-text-muted">
            {usedCredits.toLocaleString('no-NO')} / {MONTHLY_LIMIT.toLocaleString('no-NO')}
          </span>
        </div>
        <div className="settings-credit-bar-track">
          <div
            className={`settings-credit-bar-fill${usedPct > 80 ? ' settings-credit-bar-fill--danger' : ''}`}
            style={{ width: `${usedPct}%` }}
          />
        </div>
        {usedPct > 80 && (
          <div className="settings-credit-warning s-text-danger">
            <AlertCircle size={12} />
            <span>{t.settings.approachingLimit}</span>
          </div>
        )}
      </div>

      {/* Transaction history */}
      <div className="settings-section">
        <p className="settings-section-label">{t.settings.transactionHistory}</p>
        {transactions.length === 0 ? (
          <div className="s-empty-state">
            <p className="s-empty-state-desc">{t.settings.noTransactions}</p>
          </div>
        ) : (
          <div className="settings-tx-list">
            {transactions.map(tx => (
              <div key={tx.id} className="settings-tx-row s-bg-panel-2 s-border">
                <div>
                  <div className="settings-tx-desc s-text-primary">{tx.description || tx.type}</div>
                  <div className="settings-tx-date s-text-muted">
                    {new Date(tx.created_at).toLocaleDateString('no-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div className={`settings-tx-amount ${tx.amount > 0 ? 's-text-success' : 's-text-danger'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString('no-NO')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function PersonalizationContent() {
  const { settings, updateSettings } = useApp()
  const t = getTranslations(settings.language)
  const { user, refreshUser } = useAuth()
  const [name, setName] = useState(settings.userName ?? user?.name ?? '')
  const [instructions, setInstructions] = useState(settings.customInstructions ?? user?.customInstructions ?? '')
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setName(settings.userName ?? user?.name ?? '')
    setInstructions(settings.customInstructions ?? user?.customInstructions ?? '')
  }, [settings.userName, settings.customInstructions, user?.name, user?.customInstructions])

  const handleSave = async () => {
    if (saving) return
    setSaving(true)

    const trimmedName = name.trim()
    const trimmedInstructions = instructions.trim()

    updateSettings({ userName: trimmedName, customInstructions: trimmedInstructions })

    try {
      const sb = getSupabase()
      await sb.auth.updateUser({
        data: {
          ...(trimmedName ? { name: trimmedName, full_name: trimmedName } : {}),
          custom_instructions: trimmedInstructions,
          customInstructions: trimmedInstructions,
        },
      })

      if (trimmedName && user?.id) {
        await sb
          .from('profiles')
          .update({ display_name: trimmedName })
          .eq('id', user.id)
      }

      await refreshUser()
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h2 className="settings-title">{t.settings.personalization}</h2>
      <div className="settings-section">
        <p className="settings-section-label">{t.settings.aboutYou}</p>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t.settings.name}</div>
            <div className="settings-row-desc">{t.settings.nameDesc}</div>
          </div>
          <input
            type="text"
            placeholder={t.settings.yourName}
            value={name}
            onChange={e => setName(e.target.value)}
            className="s-input settings-name-input"
          />
        </div>
        <div className="settings-instructions-section">
          <div className="settings-row-label settings-instructions-label">{t.settings.customInstructions}</div>
          <div className="settings-row-desc settings-instructions-desc">
            {t.settings.customInstructionsDesc}
          </div>
          <textarea
            placeholder={t.settings.customInstructionsPlaceholder}
            rows={5}
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            className="s-input settings-instructions-textarea"
          />
        </div>
        <div className="settings-save-row">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`settings-save-btn${saved ? ' settings-save-btn--saved' : ''}`}
          >
            {saved ? t.settings.saved : t.settings.save}
          </button>
        </div>
      </div>
    </div>
  )
}

function PlaceholderContent({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="settings-title">{title}</h2>
      <div className="s-empty-state">
        <div className="settings-placeholder-icon s-bg-panel-2 s-border">
          <Zap size={20} className="s-text-muted" />
        </div>
        <p className="s-empty-state-desc">{description}</p>
      </div>
    </div>
  )
}
